/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { UserPicProps } from '../../../UserPic/UserPicManager';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './UserPicColumn';

const UserPicColumnManager: ComponentManagerFn<'UserPicColumn'> = (
  platform: Platform,
): ComponentManager<'UserPicColumn'> => {
  return {
    ...baseComponentManager<'UserPicColumn'>(platform, 'UserPicColumn'),
    type: 'UserPicColumn',
    name: 'UserPic Column',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnCellClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      ...UserPicProps,
    },
    getDisplayText: (item) => item.props.userName,
    category: 'Data Grid',
    icon: { iconName: 'user' },
    isPropertyVisible: (prop, itemData, { appUid, pageId, itemId }) => {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      switch (prop) {
        case 'toolTip':
        case 'toolTipPosition':
        case 'toolTipMaxWidth':
        case 'userName':
          return false;
        default:
          return true;
      }
    },
    replaceableWith: ['StringColumn'],
    description: `**Overview**<br/>
The UserPic Column is a visual column type designed to display user avatars within a Data Grid.
It automatically renders a user's profile image if available. If no image is found (or if the Image URL is empty), it gracefully falls back to displaying the user's initials (e.g., "JD" for John Doe) inside a colored circle. This is essential for collaborative interfaces like Task Managers, Comment Threads, or Team Directories.

**1. Data Binding**<br/>
This column connects to your user data to resolve the image and initials.
* **View Attribute:** Select the attribute containing the User ID or User Name.
* **Image URL Attribute:** (Inherited from UserPic props) Select the attribute containing the link to the profile picture (e.g., "avatar_url").
* **Name Attribute:** (Inherited from UserPic props) Select the attribute containing the full name. The component parses this string to generate the fallback initials.

**2. Configuration & Style**<br/>
The column inherits its core visual properties from the standalone UserPic component.
* **Size:** Controls the diameter of the avatar circle (Small, Medium, Large).
* **Color:** Sets the background color for the initials fallback (e.g., Primary, Secondary).
* **Dynamic Coloring:** Often used with an expression to assign different colors to different users automatically.

**3. Interaction**<br/>
* **TriggerOnCellClick:** You can add this child component to execute logic when the avatar is clicked. (Use Case: Click the avatar to open a "User Profile" modal or filter the grid to show only tasks assigned to that user).

**4. Best Practices**<br/>
* **Context:** It is common to place a **String Column** (displaying the full name) immediately to the right of the UserPic Column for clarity.
* **Fallback Strategy:** Always ensure the **Name Attribute** is mapped, even if you expect every user to have a photo. This guarantees a consistent UI if an image fails to load.`,
  };
};

export default UserPicColumnManager;
